<template>
  <div v-if="isVisible" class="modal-overlay" @click="closeModal">
    <div class="document-viewer-modal" @click.stop>
      <!-- Modal Header -->
      <div class="modal-header">
        <h3>{{ title }}</h3>
        <div class="modal-controls">
          <button v-if="documents.length > 1" class="nav-btn" @click="previousDocument" :disabled="currentIndex === 0">❮</button>
          <span v-if="documents.length > 1" class="doc-counter">{{ currentIndex + 1 }} of {{ documents.length }}</span>
          <button v-if="documents.length > 1" class="nav-btn" @click="nextDocument" :disabled="currentIndex === documents.length - 1">❯</button>
          <button class="close-btn" @click="closeModal">×</button>
        </div>
      </div>

      <!-- Document Content -->
      <div class="modal-body">
        <div v-if="currentDocument" class="document-container">
          <!-- PDF Viewer -->
          <div v-if="isPDF" class="pdf-viewer">
            <div class="document-header">
              <h4>📄 {{ currentDocument.filename || currentDocument.filepath }}</h4>
              <button class="download-btn" @click="downloadDocument">📥 Download</button>
            </div>
            <div class="document-content">
              <div v-if="!documentBlobUrl" class="pdf-loading">
                <div class="spinner"></div>
                <p>Loading PDF...</p>
              </div>
              <iframe 
                v-else
                :src="documentBlobUrl" 
                width="100%" 
                height="700px"
                frameborder="0"
                style="border: none;">
              </iframe>
            </div>
          </div>

          <!-- Excel Viewer -->
          <div v-else-if="isExcel" class="excel-viewer">
            <div class="document-header">
              <h4>📊 {{ currentDocument.filename || currentDocument.filepath }}</h4>
              <button class="download-btn" @click="downloadDocument">📥 Download</button>
            </div>
            
            <!-- Sheet Tabs -->
            <div v-if="excelSheets.length > 1" class="sheet-tabs">
              <button 
                v-for="sheet in excelSheets" 
                :key="sheet"
                @click="switchSheet(sheet)"
                :class="['sheet-tab', { active: currentSheet === sheet, loading: isLoadingExcel }]"
                :disabled="isLoadingExcel"
              >
                {{ sheet }}
              </button>
            </div>
            
            <div class="document-content">
              <div v-if="isLoadingExcel" class="excel-loading">
                <div class="spinner"></div>
                <p>Loading Excel data...</p>
              </div>
              <div v-else-if="excelData && excelData.length > 0" class="excel-content">
                <div class="sheet-info">
                  <span class="sheet-name">📋 {{ currentSheet || 'Sheet 1' }}</span>
                  <span class="row-count">{{ excelData.length }} rows</span>
                </div>
                <div class="excel-table-container">
                  <table class="excel-table">
                    <thead v-if="excelData.length > 0">
                      <tr>
                        <th v-for="(cell, cellIndex) in excelData[0]" :key="cellIndex" class="excel-header">
                          {{ cell || `Column ${cellIndex + 1}` }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, rowIndex) in excelData.slice(1)" :key="rowIndex + 1" class="excel-row">
                        <td v-for="(cell, cellIndex) in row" :key="cellIndex" class="excel-cell">
                          {{ cell }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="excel-fallback">
                <div class="fallback-content">
                  <h4>📊 Excel File</h4>
                  <p>{{ currentDocument.filename || currentDocument.filepath }}</p>
                  <p>Excel preview unavailable. Download to view the full file.</p>
                  <button class="download-btn large" @click="downloadDocument">📥 Download to View</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Image Viewer -->
          <div v-else-if="isImage" class="image-viewer">
            <img :src="documentUrl" alt="Document preview" style="max-width: 100%; height: auto;" />
          </div>

          <!-- Unsupported Format -->
          <div v-else class="unsupported-viewer">
            <div class="unsupported-info">
              <h4>📄 {{ currentDocument.filename || currentDocument.filepath }}</h4>
              <p>Preview not available for this file type.</p>
              <button class="download-btn" @click="downloadDocument">
                📥 Download File
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-else class="loading-state">
          <div class="spinner"></div>
          <p>Loading document...</p>
        </div>
      </div>

      <!-- Document Info Footer -->
      <div v-if="currentDocument" class="modal-footer">
        <div class="document-info">
          <span><strong>File:</strong> {{ currentDocument.filename || currentDocument.filepath }}</span>
          <span><strong>Size:</strong> {{ formatFileSize(currentDocument.file_size) }}</span>
          <span><strong>Upload Date:</strong> {{ formatDate(currentDocument.created_at) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  documents: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: 'Document Viewer'
  },
  initialIndex: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['close'])

const isVisible = computed(() => props.visible)
const currentIndex = ref(props.initialIndex)
const isLoadingExcel = ref(false)
const excelData = ref(null)
const excelSheets = ref([])
const currentSheet = ref('')
const documentBlobUrl = ref(null)

const currentDocument = computed(() => {
  if (props.documents.length === 0) return null
  const doc = props.documents[currentIndex.value] || props.documents[0]
  console.log(`🔍 Current document:`, doc);
  return doc
})

const apiBase = process.env.VUE_APP_ENV_API_BASE || '/api/v1/'

const documentUrl = computed(() => {
  if (!currentDocument.value) return ''
  const filename = currentDocument.value.filename || currentDocument.value.filepath || ''
  console.log(`🔍 Document URL for ${filename}:`, `${apiBase}documents/${currentDocument.value.id}/file`);
  return `${apiBase}documents/${currentDocument.value.id}/file`
})

const documentImageUrl = computed(() => {
  if (!currentDocument.value) return ''
  const filename = currentDocument.value.filename || currentDocument.value.filepath || ''
  console.log(`🔍 Document Image URL for ${filename}:`, `${apiBase}documents/${currentDocument.value.id}/image`);
  return `${apiBase}documents/${currentDocument.value.id}/image`
})

const pdfUrl = computed(() => {
  if (!currentDocument.value || !isPDF.value) return ''
  return `${documentUrl.value}#toolbar=1&navpanes=1&scrollbar=1`
})

const isPDF = computed(() => {
  if (!currentDocument.value) return false
  const filename = currentDocument.value.filename || currentDocument.value.filepath || ''
  const result = filename.toLowerCase().endsWith('.pdf')
  console.log(`🔍 File type check - PDF: ${filename} => ${result}`);
  return result
})

const isExcel = computed(() => {
  if (!currentDocument.value) return false
  const filename = currentDocument.value.filename || currentDocument.value.filepath || ''
  const result = filename.toLowerCase().match(/\.(xlsx|xls)$/)
  console.log(`🔍 File type check - Excel: ${filename} => ${!!result}`);
  return !!result
})

const isImage = computed(() => {
  if (!currentDocument.value) return false
  const filename = currentDocument.value.filename || currentDocument.value.filepath || ''
  const result = filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)
  console.log(`🔍 File type check - Image: ${filename} => ${!!result}`);
  return !!result
})

function closeModal() {
  // Clean up blob URL to prevent memory leaks
  if (documentBlobUrl.value) {
    URL.revokeObjectURL(documentBlobUrl.value)
    documentBlobUrl.value = null
  }
  emit('close')
}

function nextDocument() {
  if (currentIndex.value < props.documents.length - 1) {
    currentIndex.value++
  }
}

function previousDocument() {
  if (currentIndex.value > 0) {
    currentIndex.value--
  }
}

function downloadDocument() {
  if (currentDocument.value) {
    const link = document.createElement('a')
    link.href = documentUrl.value
    link.download = currentDocument.value.filename || currentDocument.value.filepath || 'document'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

async function loadDocumentBlob() {
  if (!currentDocument.value) return
  
  try {
    console.log(`📄 Loading document blob for ${currentDocument.value.id}`)
    
    const response = await fetch(documentUrl.value, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      }
    })
    
    if (response.ok) {
      const blob = await response.blob()
      if (documentBlobUrl.value) {
        URL.revokeObjectURL(documentBlobUrl.value)
      }
      documentBlobUrl.value = URL.createObjectURL(blob)
      console.log(`📄 Document blob loaded successfully`)
    } else {
      console.warn(`📄 Document blob failed: ${response.status}`)
      documentBlobUrl.value = null
    }
  } catch (error) {
    console.error('📄 Error loading document blob:', error)
    documentBlobUrl.value = null
  }
}

async function loadExcelData() {
  if (!currentDocument.value || !isExcel.value) return
  
  try {
    isLoadingExcel.value = true
    console.log(`📊 Loading Excel data for document ${currentDocument.value.id}`)
    
    // Try to get Excel preview data from the backend
    const response = await fetch(`${apiBase}excel_preview/${currentDocument.value.id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      console.log(`📊 Excel data loaded:`, data)
      
      // Handle sheet information
      if (data.available_sheets && Array.isArray(data.available_sheets)) {
        excelSheets.value = data.available_sheets
        currentSheet.value = data.current_sheet || data.available_sheets[0]
        console.log(`📊 Available sheets: ${data.available_sheets.join(', ')}`)
        console.log(`📊 Current sheet: ${currentSheet.value}`)
      }
      
      if (data.data && Array.isArray(data.data)) {
        excelData.value = data.data
        console.log(`📊 Successfully loaded ${data.data.length} Excel rows`)
      } else if (data.rows && Array.isArray(data.rows)) {
        excelData.value = data.rows
        console.log(`📊 Successfully loaded ${data.rows.length} Excel rows (legacy format)`)
      } else {
        console.warn('📊 No data/rows found in response:', Object.keys(data))
        excelData.value = []
      }
    } else {
      console.warn(`📊 Excel preview failed: ${response.status}`)
      excelData.value = []
    }
  } catch (error) {
    console.error('📊 Error loading Excel data:', error)
    excelData.value = []
  } finally {
    isLoadingExcel.value = false
  }
}

async function switchSheet(sheetName) {
  if (!currentDocument.value || currentSheet.value === sheetName) return
  
  try {
    isLoadingExcel.value = true
    currentSheet.value = sheetName
    console.log(`📊 Switching to sheet: ${sheetName}`)
    
    const response = await fetch(`${apiBase}excel_preview/${currentDocument.value.id}?sheet=${encodeURIComponent(sheetName)}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      console.log(`📊 Sheet data loaded for ${sheetName}:`, data)
      
      if (data.data && Array.isArray(data.data)) {
        excelData.value = data.data
        console.log(`📊 Successfully loaded ${data.data.length} rows for sheet ${sheetName}`)
      } else {
        console.warn('📊 No data found for sheet:', sheetName)
        excelData.value = []
      }
    } else {
      console.warn(`📊 Sheet switch failed: ${response.status}`)
    }
  } catch (error) {
    console.error('📊 Error switching sheet:', error)
  } finally {
    isLoadingExcel.value = false
  }
}

function formatFileSize(bytes) {
  if (!bytes) return 'Unknown'
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
}

function formatDate(dateString) {
  if (!dateString) return 'Unknown'
  return new Date(dateString).toLocaleDateString()
}

// Reset index when documents change
watch(() => props.documents, () => {
  currentIndex.value = Math.min(props.initialIndex, props.documents.length - 1)
})

// Load document data when document changes
watch(() => currentDocument.value, () => {
  // Clear previous data
  excelData.value = null
  excelSheets.value = []
  currentSheet.value = ''
  if (documentBlobUrl.value) {
    URL.revokeObjectURL(documentBlobUrl.value)
    documentBlobUrl.value = null
  }
  
  if (currentDocument.value) {
    // Load blob for PDF display
    loadDocumentBlob()
    
    // Load Excel data if it's an Excel file
    if (isExcel.value) {
      loadExcelData()
    }
  }
})

// Handle escape key
function handleKeydown(event) {
  if (event.key === 'Escape' && isVisible.value) {
    closeModal()
  } else if (event.key === 'ArrowLeft' && isVisible.value) {
    previousDocument()
  } else if (event.key === 'ArrowRight' && isVisible.value) {
    nextDocument()
  }
}

// Add event listeners
import { onMounted, onUnmounted } from 'vue'

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.document-viewer-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 1200px;
  height: 90%;
  max-height: 900px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.modal-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.nav-btn {
  background: #4f46e5;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.2s;
}

.nav-btn:hover:not(:disabled) {
  background: #3730a3;
}

.nav-btn:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}

.doc-counter {
  font-size: 14px;
  color: #6b7280;
  min-width: 60px;
  text-align: center;
}

.close-btn {
  background: #ef4444;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  transition: background 0.2s;
}

.close-btn:hover {
  background: #dc2626;
}

.modal-body {
  flex: 1;
  overflow: auto;
  padding: 0;
}

.document-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-viewer,
.excel-viewer {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.document-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.document-header h4 {
  margin: 0;
  color: #1f2937;
  font-size: 1rem;
  font-weight: 500;
}

.document-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 600px;
}

.document-content iframe,
.document-content object {
  flex: 1;
  border: none;
  width: 100%;
  height: 100%;
}

.excel-loading,
.pdf-loading {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 400px;
  color: #6b7280;
}

.excel-loading .spinner,
.pdf-loading .spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #4f46e5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.sheet-tabs {
  display: flex;
  gap: 2px;
  padding: 10px 20px 0;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
}

.sheet-tab {
  background: #e5e7eb;
  color: #6b7280;
  border: none;
  padding: 8px 16px;
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s;
  border-bottom: 2px solid transparent;
}

.sheet-tab:hover:not(:disabled) {
  background: #d1d5db;
  color: #374151;
}

.sheet-tab.active {
  background: white;
  color: #1f2937;
  border-bottom: 2px solid #4f46e5;
  font-weight: 600;
}

.sheet-tab:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.excel-content {
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.sheet-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #6b7280;
}

.sheet-name {
  font-weight: 600;
  color: #374151;
}

.row-count {
  font-size: 12px;
}

.excel-table-container {
  flex: 1;
  overflow: auto;
  background: white;
  margin: 0;
}

.excel-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: white;
}

.excel-header {
  background: #f3f4f6;
  color: #374151;
  font-weight: 600;
  padding: 12px 8px;
  border: 1px solid #d1d5db;
  text-align: left;
  position: sticky;
  top: 0;
  z-index: 10;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.excel-row:nth-child(even) {
  background: #f9fafb;
}

.excel-row:hover {
  background: #f3f4f6;
}

.excel-cell {
  border: 1px solid #e5e7eb;
  padding: 8px 8px;
  text-align: left;
  background: inherit;
  word-break: break-word;
  max-width: 200px;
  font-size: 12px;
  line-height: 1.4;
}

.excel-cell:empty::after {
  content: "—";
  color: #d1d5db;
}

.excel-fallback {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

.fallback-content {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.fallback-content h4 {
  margin: 0 0 10px 0;
  color: #1f2937;
  font-size: 1.2rem;
  font-weight: 600;
}

.fallback-content p {
  margin: 5px 0;
  color: #6b7280;
  font-size: 0.9rem;
}

.download-btn.large {
  margin-top: 20px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
}





.unsupported-viewer {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background: #f9fafb;
}

.excel-info,
.unsupported-info {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.excel-info h4,
.unsupported-info h4 {
  margin: 0 0 15px 0;
  color: #1f2937;
  font-size: 1.1rem;
}

.excel-info p,
.unsupported-info p {
  margin: 0 0 20px 0;
  color: #6b7280;
}

.download-btn {
  background: #10b981;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s;
}

.download-btn:hover {
  background: #059669;
}

.image-viewer {
  padding: 20px;
  text-align: center;
  height: 100%;
  overflow: auto;
}

.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6b7280;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #4f46e5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.modal-footer {
  padding: 15px 25px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.document-info {
  display: flex;
  gap: 25px;
  font-size: 13px;
  color: #6b7280;
  flex-wrap: wrap;
}

.document-info span {
  white-space: nowrap;
}

.document-info strong {
  color: #374151;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .document-viewer-modal {
    width: 95%;
    height: 95%;
  }
  
  .modal-header {
    padding: 15px 20px;
  }
  
  .modal-header h3 {
    font-size: 1.1rem;
  }
  
  .modal-controls {
    gap: 10px;
  }
  
  .document-info {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
